/**
 * The shared signature panel — a slide-in from the right used by all three
 * document views, opened from a signed-container row. It answers one question:
 * what is sealed inside this container, and who signed it.
 *
 * Its chrome deliberately mirrors the PM issue detail panel (WHA-3810): a quiet
 * eyebrow + close header over a full-bleed hairline, a body of unruled bands
 * on one 20px rhythm, labelled fields with a status pill, and a footer button
 * row — the header and footer rules are the only horizontal lines. Same vocabulary, so the app has one side-panel language.
 *
 * Built on the Radix dialog primitive directly rather than `@/components/ui/dialog`,
 * whose content is hard-centred; everything else (overlay, focus trap, escape
 * handling) is the same primitive the app already ships.
 */

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { format } from "date-fns";
import { Download, Eye, Loader2, Share2, Signature, X } from "lucide-react";
import type { ReactNode } from "react";
import { toast } from "sonner";
import { useSignatureInspection } from "../../signing/useSignatureInspection";
import { inspectedDocumentInfo } from "../../signing/signatureInspection";
import { SignatureInspectionStatus } from "../molecules/SignatureInspectionStatus";

import { SettingsSectionLabel } from "@/components/settings/SettingsSectionLabel";
import { Button } from "@/components/ui/button";
import { FileTypeIcon } from "@/components/ui/documents/file-type-icon";
import { Separator } from "@/components/ui/separator";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";
import { downloadDocument } from "@/stores/documents/repository";
import { formatFileSize } from "@/utils/formatFileSize";
import { SignatureStatusChip, SignedFileIcon } from "../atoms/SignedContainerIndicators";
import {
  canOpenEnclosedDocument,
  type ContainerSignature,
  type EnclosedDocument,
  type SignedContainerInfo,
} from "../../signing/signatureModel";
import type {
  SignaturePanelActions,
  SignaturePanelTarget,
} from "../../signing/signaturePanelContext";
import {
  useEnclosedDocumentsQuery,
  useSignedContainerInfoQuery,
} from "../../signing/useSignedContainerInfo";

interface SignaturePanelProps extends SignaturePanelActions {
  target: SignaturePanelTarget | null;
  onClose: () => void;
  /** Opens an enclosed document in the viewer. Supplied by the provider, which
   *  owns the viewer dialog; absent only in tests that render the panel bare. */
  onOpenDocument?: OpenHandler;
}

type DownloadHandler = (id: string, filename: string) => void;
type OpenHandler = (doc: EnclosedDocument) => void;

/** The enclosed-documents query as the body renders it — one value rather than
 *  four props threaded through every level. */
interface EnclosedState {
  docs: EnclosedDocument[];
  isError: boolean;
  isPending: boolean;
  /** The document the panel was opened on, highlighted in the list. */
  highlightedId?: string | null;
}

/** What the body can do with an enclosed document: open it, download it. */
interface EnclosedActions {
  onDownload: DownloadHandler;
  onOpen?: OpenHandler;
}

/** Downloads a document, surfacing a toast rather than throwing into the panel. */
function useDownloadHandler(): DownloadHandler {
  const { t } = useLanguage();
  const downloadFailed = t.documents.signedContainer.panel.downloadFailed;

  return (id, filename) => {
    downloadDocument(id, filename, { surface: "signing" }).catch((err) => {
      console.error("Signature panel download failed:", err);
      toast.error(downloadFailed);
    });
  };
}

/** The panel's section label — the issue panel's metrics, same grey. */
function PanelLabel({ children }: { children: ReactNode }) {
  return (
    <SettingsSectionLabel className="text-[10px] tracking-wide">{children}</SettingsSectionLabel>
  );
}

/** A labelled band in the body — the label carries its own count suffix. */
function PanelSection({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: ReactNode;
}) {
  return (
    <section className="py-5">
      <PanelLabel>
        {title}
        {count > 0 && <span> · {count}</span>}
      </PanelLabel>
      {children}
    </section>
  );
}

export function SignaturePanel({
  target,
  onClose,
  onShare,
  onSign,
  onOpenDocument,
}: SignaturePanelProps) {
  return (
    <DialogPrimitive.Root
      open={target !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className="fixed inset-y-0 right-0 z-50 flex h-full w-full max-w-lg flex-col border-l bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right"
          aria-describedby={undefined}
        >
          {target && (
            <PanelContents
              target={target}
              onShare={onShare}
              onSign={onSign}
              onOpenDocument={onOpenDocument}
            />
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/** Mounted only while the panel is open, so its queries run on demand. */
function PanelContents({
  target,
  onShare,
  onSign,
  onOpenDocument,
}: {
  target: SignaturePanelTarget;
  onOpenDocument?: OpenHandler;
} & SignaturePanelActions) {
  const infoQuery = useSignedContainerInfoQuery(target.containerId);
  const enclosedQuery = useEnclosedDocumentsQuery(target.containerId);
  const handleDownload = useDownloadHandler();
  const inspection = useSignatureInspection(target.containerId);
  const inspected = inspection.data;
  const info =
    inspected && (inspected.state === "complete" || !infoQuery.data)
      ? inspectedDocumentInfo(inspected)
      : infoQuery.data;

  return (
    <>
      <PanelHeader />
      <Separator />

      <div className="min-h-0 flex-1 overflow-y-auto">
        <SignatureInspectionStatus inspection={inspection} />
        <PanelBody
          info={info}
          enclosed={{
            docs: enclosedQuery.data ?? [],
            isError: enclosedQuery.isError,
            isPending: enclosedQuery.isPending,
            highlightedId: target.enclosedDocumentId,
          }}
          openedFromFilename={target.enclosedFilename}
          isLoading={infoQuery.isPending || enclosedQuery.isPending || inspection.isPending}
          actions={{ onDownload: handleDownload, onOpen: onOpenDocument }}
        />
      </div>

      <PanelFooter info={info} onDownload={handleDownload} onShare={onShare} onSign={onSign} />
    </>
  );
}

/** Eyebrow + close, on the body's horizontal padding (issue-panel header). */
function PanelHeader() {
  const { t } = useLanguage();
  const tp = t.documents.signedContainer.panel;

  return (
    <div className="flex flex-shrink-0 items-center justify-between gap-2 px-5 py-4">
      {/* The dialog's accessible name lives on the always-rendered eyebrow, so
          Content has a Title even while the body is still loading or errored. */}
      <DialogPrimitive.Title className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {tp.eyebrow}
      </DialogPrimitive.Title>
      <DialogPrimitive.Close
        aria-label={tp.close}
        className="rounded-sm p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </DialogPrimitive.Close>
    </div>
  );
}

interface PanelBodyProps {
  info?: SignedContainerInfo;
  enclosed: EnclosedState;
  openedFromFilename?: string | null;
  isLoading: boolean;
  actions: EnclosedActions;
}

function PanelBody({ info, enclosed, openedFromFilename, isLoading, actions }: PanelBodyProps) {
  const { t } = useLanguage();
  const tp = t.documents.signedContainer.panel;

  if (!info) {
    return isLoading ? (
      <div className="flex items-center gap-2 px-5 py-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        {tp.loading}
      </div>
    ) : (
      <p className="px-5 py-4 text-sm text-muted-foreground">{tp.loadError}</p>
    );
  }

  return (
    <PanelBands
      info={info}
      enclosed={enclosed}
      openedFromFilename={openedFromFilename}
      actions={actions}
    />
  );
}

interface PanelBandsProps {
  info: SignedContainerInfo;
  enclosed: EnclosedState;
  openedFromFilename?: string | null;
  actions: EnclosedActions;
}

/** The resolved body: title, fields, and the two labelled bands. */
function PanelBands({ info, enclosed, openedFromFilename, actions }: PanelBandsProps) {
  const { t } = useLanguage();
  const tp = t.documents.signedContainer.panel;

  return (
    <div className="px-5 pb-6">
      <ContainerTitleBand info={info} />
      {openedFromFilename && (
        <p className="py-4 text-sm text-muted-foreground">
          {tp.openedFrom
            .replace("{document}", openedFromFilename)
            .replace("{container}", info.containerFilename)}
        </p>
      )}
      <ContainerFields info={info} />

      {!info.containerFilename.toLowerCase().endsWith(".pdf") && (
        <PanelSection title={tp.enclosedTitle} count={enclosed.docs.length}>
          <EnclosedDocumentList enclosed={enclosed} actions={actions} />
        </PanelSection>
      )}

      <PanelSection title={tp.signaturesTitle} count={info.signatures.length}>
        <SignatureList signatures={info.signatures} inspectionComplete={info.inspectionComplete} />
      </PanelSection>
    </div>
  );
}

/** Seal, container filename and its size — the subject of the panel. */
function ContainerTitleBand({ info }: { info: SignedContainerInfo }) {
  const { t } = useLanguage();
  const ts = t.documents.signedContainer;

  return (
    <div className="flex items-start gap-3 py-5">
      <SignedFileIcon
        filename={info.containerFilename}
        status={info.status}
        className="mt-0.5 flex-shrink-0"
      />
      <div className="min-w-0 flex-1">
        <p className="break-words text-sm font-semibold">{info.containerFilename}</p>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          {ts.panel.fileType.replace(
            "{type}",
            info.containerFilename.split(".").at(-1)?.toUpperCase() ?? ""
          )}
          {info.containerSize ? ` · ${formatFileSize(info.containerSize)}` : ""}
        </p>
      </div>
    </div>
  );
}

/**
 * Labelled metadata row — status pill first, exactly like the issue panel.
 * The document count lives on the Documents section header instead of here,
 * and "signed on" is omitted entirely when Franklin holds no signing date,
 * so the row stays one line rather than wrapping around an em dash.
 */
function ContainerFields({ info }: { info: SignedContainerInfo }) {
  const { t } = useLanguage();
  const tp = t.documents.signedContainer.panel;
  const lastSigned = info.signatures.at(-1)?.signedAt;

  return (
    <div className="flex flex-wrap gap-x-6 gap-y-4 py-5">
      <PanelField label={tp.fieldStatus} className="min-w-[110px]">
        <SignatureStatusChip status={info.status} />
      </PanelField>
      <PanelField
        label={tp.fieldSignatures}
        className={info.signatures.length > 0 ? "min-w-[90px]" : "min-w-[180px]"}
      >
        {/* Franklin holds no signature records for a container signed elsewhere.
            Rendering "0" would read as "nobody signed it", which is false — say
            what is actually true instead. */}
        {info.signatures.length > 0 || info.inspectionComplete ? (
          <span className="text-sm">{info.signatures.length}</span>
        ) : (
          <span className="text-sm text-muted-foreground">{tp.fieldSignaturesUnknown}</span>
        )}
      </PanelField>
      {lastSigned && (
        <PanelField label={tp.fieldSignedOn} className="min-w-[150px]">
          <span className="text-sm">{formatSignedAt(lastSigned)}</span>
        </PanelField>
      )}
    </div>
  );
}

function PanelField({
  label,
  className,
  children,
}: {
  label: string;
  className: string;
  children: ReactNode;
}) {
  return (
    <div className={cn("flex-1", className)}>
      <PanelLabel>{label}</PanelLabel>
      <div className="mt-1.5 flex min-h-6 items-center">{children}</div>
    </div>
  );
}

function EnclosedDocumentList({
  enclosed,
  actions,
}: {
  enclosed: EnclosedState;
  actions: EnclosedActions;
}) {
  const { docs, isError, isPending, highlightedId } = enclosed;
  const { t } = useLanguage();
  const tp = t.documents.signedContainer.panel;

  if (isError) {
    return <p className="mt-1.5 text-sm text-destructive">{tp.enclosedError}</p>;
  }

  // The info query resolves in one round trip, the enclosed one in 2–3 — while
  // it is still in flight the band must not claim "nothing inside".
  if (isPending) {
    return (
      <div className="mt-1.5 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        {tp.loading}
      </div>
    );
  }

  if (docs.length === 0) {
    return <p className="mt-1.5 text-sm text-muted-foreground">{tp.enclosedEmpty}</p>;
  }

  return (
    <ul className="mt-1.5 space-y-0.5">
      {docs.map((doc) => (
        <EnclosedDocumentEntry
          key={doc.id}
          doc={doc}
          isHighlighted={doc.id === highlightedId}
          actions={actions}
        />
      ))}
    </ul>
  );
}

/**
 * One enclosed document: the name opens it (as clicking a document name does in
 * every other view), with the eye and download icons spelling both actions out.
 * A document that cannot be previewed (or whose file Franklin no longer holds)
 * keeps its name as plain text rather than a control that would do nothing.
 */
function EnclosedDocumentEntry({
  doc,
  isHighlighted,
  actions,
}: {
  doc: EnclosedDocument;
  isHighlighted: boolean;
  actions: EnclosedActions;
}) {
  const { t } = useLanguage();
  const tp = t.documents.signedContainer.panel;
  const canOpen = !!actions.onOpen && canOpenEnclosedDocument(doc);
  const open = () => actions.onOpen?.(doc);

  return (
    <li
      className={cn(
        // The PM issue panel's attached-document row: a plain white row whose
        // border only appears on hover. The document the panel was opened
        // from keeps it.
        "group flex items-center gap-2 rounded-md border px-2 py-1.5 hover:border-foreground/40",
        isHighlighted ? "border-foreground/40" : "border-transparent"
      )}
    >
      <FileTypeIcon filename={doc.filename} className="h-6 w-6" />
      {canOpen ? (
        <button
          type="button"
          onClick={open}
          className="min-w-0 flex-1 truncate text-left text-xs hover:underline focus:underline focus:outline-none"
          title={doc.filename}
        >
          {doc.filename}
        </button>
      ) : (
        <span className="min-w-0 flex-1 truncate text-xs" title={doc.filename}>
          {doc.filename}
        </span>
      )}
      <span className="flex-shrink-0 text-[10px] text-muted-foreground">
        {formatFileSize(doc.size ?? 0)}
      </span>
      {canOpen && (
        <button
          type="button"
          onClick={open}
          aria-label={`${tp.open} ${doc.filename}`}
          title={tp.open}
          className="flex-shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <Eye className="h-3.5 w-3.5" />
        </button>
      )}
      <button
        type="button"
        onClick={() => actions.onDownload(doc.id, doc.filename)}
        aria-label={`${tp.download} ${doc.filename}`}
        title={tp.download}
        className="flex-shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <Download className="h-3.5 w-3.5" />
      </button>
    </li>
  );
}

function SignatureList({
  signatures,
  inspectionComplete,
}: {
  signatures: ContainerSignature[];
  inspectionComplete?: boolean;
}) {
  const { t } = useLanguage();
  const tp = t.documents.signedContainer.panel;

  if (signatures.length === 0) {
    return (
      <p className="mt-1.5 text-sm text-muted-foreground">
        {inspectionComplete ? tp.noSignatures : tp.signaturesEmpty}
      </p>
    );
  }

  return (
    <ul className="mt-1.5 space-y-0.5">
      {signatures.map((signature) => (
        <SignatureEntry key={signature.id} signature={signature} />
      ))}
    </ul>
  );
}

function SignatureEntry({ signature }: { signature: ContainerSignature }) {
  const { t } = useLanguage();
  const tp = t.documents.signedContainer.panel;
  const method = [
    signature.method,
    signature.signatureType ? signature.signatureType.toUpperCase() : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <li className="px-2 py-1.5">
      <div className="flex items-center justify-between gap-2">
        <p className="truncate text-xs font-medium">{signature.signerName || tp.unknownSigner}</p>
        {signature.validity === "invalid" && (
          <span className="flex-shrink-0 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
            {tp.signatureBroken}
          </span>
        )}
        {signature.validity === "valid" && (
          <span className="text-[10px] text-muted-foreground">{tp.signaturePassed}</span>
        )}
      </div>
      <p className="mt-0.5 text-[10px] text-muted-foreground">
        {[
          signature.signedAt
            ? tp.signedOn.replace("{date}", formatSignedAt(signature.signedAt))
            : tp.unknownTime,
          method,
          signature.signerCountry,
        ]
          .filter(Boolean)
          .join(" · ")}
      </p>
      {signature.timestampAt && (
        <p className="text-[10px] text-muted-foreground">
          {tp.timestampOn.replace("{date}", formatSignedAt(signature.timestampAt))}
        </p>
      )}
      {!!signature.warningCount && (
        <p className="text-[10px] text-amber-700">
          {tp.signatureWarnings.replace("{count}", String(signature.warningCount))}
        </p>
      )}
    </li>
  );
}

/** Add signature · Share · Download — the panel's action row. */
function PanelFooter({
  info,
  onDownload,
  onShare,
  onSign,
}: {
  info?: SignedContainerInfo;
  onDownload: DownloadHandler;
} & SignaturePanelActions) {
  const { t } = useLanguage();
  const tp = t.documents.signedContainer.panel;

  if (!info) return null;

  return (
    <div className="flex flex-wrap gap-2 border-t px-5 py-4">
      {onSign && (
        <Button
          size="sm"
          onClick={() =>
            onSign({
              id: info.containerId,
              filename: info.containerFilename,
              size: info.containerSize,
            })
          }
        >
          <Signature className="mr-2 h-4 w-4" />
          {tp.sign}
        </Button>
      )}
      {onShare && (
        <Button variant="outline" size="sm" onClick={() => onShare(info.containerId)}>
          <Share2 className="mr-2 h-4 w-4" />
          {tp.share}
        </Button>
      )}
      <Button
        variant="outline"
        size="sm"
        onClick={() => onDownload(info.containerId, info.containerFilename)}
      >
        <Download className="mr-2 h-4 w-4" />
        {tp.download}
      </Button>
    </div>
  );
}

/** Only display a UTC offset when the provider supplied a timezone. */
function formatSignedAt(signedAt: string): string {
  try {
    const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(signedAt);
    return format(new Date(signedAt), hasTimezone ? "dd-MM-yyyy HH:mm OOOO" : "dd-MM-yyyy HH:mm");
  } catch {
    return signedAt;
  }
}
